import React, { useState } from "react";
import { TextField, Button, Alert, Autocomplete, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Dropzone from "react-dropzone";
import Resizer from "react-image-file-resizer";
import toast, { LoaderIcon } from "react-hot-toast";
import { appTypes } from "@utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthStore } from "@state/auth";
import { apiClient } from "@api/index";
import NavBar from "@components/NavBar";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingButton } from "@mui/lab";
import { hasPermission } from "@utils/permissions";

const AppSettings = () => {
  const params = useParams();
  const { appID } = params;
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canDelete = hasPermission(user.roles, "manage_ott", "delete");
  const canCreate = hasPermission(user.roles, "manage_ott", "create");

  const [appName, setAppName] = useState<string>("");
  const [initialAppName, setInitialAppName] = useState<string>("");
  const [appNameError, setAppNameError] = useState<string>("");
  const [appTargets, setFormAppTargets] = useState<{ title: string; value: string }[] | []>([]);
  const [appTargetsError, setAppTargetsError] = useState<string>("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoProcessing, setLogoProcessing] = useState<boolean>(false);
  const [transparentIconFile, setTransparentIconFile] = useState<File | null>(null);
  const [transparentIconPreview, setTransparentIconPreview] = useState<string | null>(null);
  const [transparentIconProcessing, setTransparentIconProcessing] = useState<boolean>(false);
  const [splashFile, setSplashFile] = useState<File | null>(null);
  const [splashPreview, setSplashPreview] = useState<string | null>(null);
  const [splashProcessing, setSplashProcessing] = useState<boolean>(false);
  const [resizedImages, setResizedImages] = useState<any>({});
  const [resizedTransparentIcons, setResizedTransparentIcons] = useState<any>({});
  const [fireTVIconBgColor, setFireTVIconBgColor] = useState<string>("#000000");
  const [fireTVIconBgColorError, setFireTVIconBgColorError] = useState<string>("");

  // Android TV custom images
  const [androidTVBannerFile, setAndroidTVBannerFile] = useState<File | null>(null);
  const [androidTVBannerPreview, setAndroidTVBannerPreview] = useState<string | null>(null);
  const [androidTVBannerProcessing, setAndroidTVBannerProcessing] = useState<boolean>(false);

  const [androidTVTVBannerFile, setAndroidTVTVBannerFile] = useState<File | null>(null);
  const [androidTVTVBannerPreview, setAndroidTVTVBannerPreview] = useState<string | null>(null);
  const [androidTVTVBannerProcessing, setAndroidTVTVBannerProcessing] = useState<boolean>(false);

  const [androidTVStoreListingFile, setAndroidTVStoreListingFile] = useState<File | null>(null);
  const [androidTVStoreListingPreview, setAndroidTVStoreListingPreview] = useState<string | null>(null);
  const [androidTVStoreListingProcessing, setAndroidTVStoreListingProcessing] = useState<boolean>(false);
  const [showDeleteAppModal, setShowDeleteAppModal] = useState<boolean>(false);
  const [showSaveChangesModal, setShowSaveChangesModal] = useState<boolean>(false);
  const [imagePreviewModal, setImagePreviewModal] = useState<{ open: boolean; imageUrl: string | null; title: string }>({
    open: false,
    imageUrl: null,
    title: "",
  });

  const { data, isLoading } = apiClient.ott.getAppByID.useQuery(
    {
      appID: appID as string,
      clientID: user.clientID,
    },
    {
      refetchOnWindowFocus: false,
      onSuccess(res) {
        const { manifest, appName, targets } = res;

        const { data } = manifest;
        const { theme } = data;

        setAppName(appName);
        setInitialAppName(appName);
        setFormAppTargets(
          targets.map((target: string) => {
            let title = "";
            let value = "";

            switch (target) {
              case "SamsungTV":
                title = "Samsung TV";
                value = appTypes.samsungTv;
                break;
              case "AppleTV":
                title = "Apple TV";
                value = appTypes.appleTv;
                break;
              case "AndroidTV":
                title = "Android TV";
                value = appTypes.androidTv;
                break;
              case "FireTV":
                title = "Fire TV";
                value = appTypes.fireTv;
                break;
              default:
                break;
            }

            return {
              title,
              value,
            };
          }),
        );
      },
    },
  );

  const { mutateAsync: updateAppNameAndTargets, isPending: updateAppNameAndTargetsIsLoading } = apiClient.ott.updateAppNameAndTargets.useMutation({
    onError: (err) => {
      toast.error(err as string);
    },
  });

  const { mutateAsync: buildApp, isPending: buildAppIsLoading } = apiClient.ott.buildApp.useMutation({
    onError: (err) => {
      toast.error("Error building app");
    },
  });

  const appStatus = data?.manifest?.data?.status;
  const { mutateAsync: deleteAppAsync, isPending } = apiClient.ott.deleteApp.useMutation();

  const handleAppNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAppName(value);
    validateAppName(value);
  };

  const validateAppName = (name: string) => {
    const trimmedName = name.trim();
    const regex = /^[A-Za-z0-9 ]+$/;

    if (!trimmedName) {
      setAppNameError("Application name is required.");
      return false;
    } else if (!regex.test(trimmedName)) {
      setAppNameError("Application name can only contain letters, numbers, and spaces.");
      return false;
    } else {
      setAppNameError("");
      return true;
    }
  };

  const handleAppTargetsChange = (event: any, newValues: { title: string; value: string }[]) => {
    setFormAppTargets(newValues);
    if (newValues.length === 0) {
      setAppTargetsError("At least one target platform is required.");
    } else {
      setAppTargetsError("");
    }
  };

  const hasPendingChanges = () => {
    if (appName !== initialAppName) return true;
    const initialTargets = JSON.stringify(appTargets);
    const currentTargets = JSON.stringify(appTargets);
    if (currentTargets !== initialTargets) return true;
    if (logoFile !== null) return true;
    if (transparentIconFile !== null) return true;
    if (splashFile !== null) return true;
    if (androidTVBannerFile !== null) return true;
    if (androidTVTVBannerFile !== null) return true;
    if (androidTVStoreListingFile !== null) return true;
    return false;
  };

  const isFireTVSelected = () => {
    return appTargets.some((target) => target.value === appTypes.fireTv);
  };

  const isAndroidTVSelected = () => {
    return appTargets.some((target) => target.value === appTypes.androidTv);
  };

  const validateFireTVIconBgColor = (color: string) => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!color.trim()) {
      setFireTVIconBgColorError("Icon background color is required for FireTV.");
      return false;
    } else if (!hexRegex.test(color)) {
      setFireTVIconBgColorError("Please enter a valid hex color (e.g., #000000).");
      return false;
    } else {
      setFireTVIconBgColorError("");
      return true;
    }
  };

  const handleFireTVIconBgColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFireTVIconBgColor(value);
    if (isFireTVSelected()) {
      validateFireTVIconBgColor(value);
    }
  };

  const isFormValid = () => {
    const basicValidation = !appNameError && !appTargetsError && appName.trim() !== "" && appTargets.length > 0;
    if (isFireTVSelected()) {
      const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      const isColorValid = fireTVIconBgColor.trim() !== "" && hexRegex.test(fireTVIconBgColor);
      return basicValidation && !fireTVIconBgColorError && isColorValid;
    }
    return basicValidation;
  };

  const handleSaveChanges = () => {
    const isNameValid = validateAppName(appName);
    const isTargetsValid = appTargets.length > 0;
    let isFireTVValid = true;

    if (!isTargetsValid) {
      setAppTargetsError("At least one target platform is required.");
    }

    if (isFireTVSelected()) {
      isFireTVValid = validateFireTVIconBgColor(fireTVIconBgColor);
    }

    if (isNameValid && isTargetsValid && isFireTVValid) {
      if (hasPendingChanges()) {
        setShowSaveChangesModal(true);
      }
    }
  };

  const confirmSaveChanges = () => {
    setShowSaveChangesModal(false);

    const appData: any = {
      appID: appID as string,
      clientID: user.clientID,
      appName,
      targets: appTargets.map((target) => target.value),
    };

    // Add FireTV settings if FireTV is selected
    if (isFireTVSelected()) {
      appData.fireTVIconBgColor = fireTVIconBgColor;
    }

    updateAppNameAndTargets({
      appID: appID as string,
      clientID: user.clientID,
      appName,
      targets: appData.targets,
    })
      .then(() => {
        const formData = new FormData();

        // Add primary icon resized images
        Object.entries(resizedImages).forEach(([name, file]: any) => {
          const renamedFile = new File([file], `icon_${name}.png`, { type: "image/png" });
          formData.append("media", renamedFile);
        });

        // Add transparent icon resized images
        Object.entries(resizedTransparentIcons).forEach(([name, file]: any) => {
          const renamedFile = new File([file], `transparent_icon_${name}.png`, { type: "image/png" });
          formData.append("media", renamedFile);
        });

        if (splashFile) {
          formData.append("media", splashFile);
        }

        // Add Android TV custom images
        if (androidTVBannerFile) {
          formData.append("media", androidTVBannerFile);
        }
        if (androidTVTVBannerFile) {
          formData.append("media", androidTVTVBannerFile);
        }
        if (androidTVStoreListingFile) {
          formData.append("media", androidTVStoreListingFile);
        }

        const appDataBlob = new Blob([JSON.stringify(appData)], { type: "application/json" });
        const appDataFile = new File([appDataBlob], "appConfig.json", { type: "application/json" });
        formData.append("media", appDataFile);

        if (appStatus === "draft" || appStatus === "pending") {
          buildApp({
            appID: appID as string,
            clientID: user.clientID,
            targets: appData.targets,
            formData,
          }).then(() => {
            queryClient.invalidateQueries();
            toast.success(`app ${appStatus === "draft" ? "submitted for review" : "changes saved"}`);
          });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Couldn't update application");
      });
  };

  const handleLogoDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setLogoProcessing(true);
      setLogoFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);

      resizeAndSetImages(file, false);
    }
  };

  const handleTransparentIconDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setTransparentIconProcessing(true);
      setTransparentIconFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setTransparentIconPreview(previewUrl);

      resizeAndSetImages(file, true);
    }
  };

  const handleSplashDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setSplashProcessing(true);
      const img = new Image();
      const previewUrl = URL.createObjectURL(file);
      img.src = previewUrl;
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const tolerance = 0.05;
        const targetRatio = 16 / 9;
        if (Math.abs(aspectRatio - targetRatio) > tolerance) {
          toast.error("Splash screen must have a 16:9 aspect ratio.");
          setSplashProcessing(false);
          URL.revokeObjectURL(previewUrl);
        } else {
          setSplashFile(new File([file], "splash.png", { type: "image/png" }));
          setSplashPreview(previewUrl);
          setSplashProcessing(false);
        }
      };
    }
  };

  const resizeAndSetImages = (file: File, isTransparent: boolean) => {
    const icons = [
      { name: "ic_launcher_mipmap-mdpi", width: 48, height: 48 },
      { name: "ic_launcher_mipmap-hdpi", width: 72, height: 72 },
      { name: "ic_launcher_mipmap-xhdpi", width: 96, height: 96 },
      { name: "ic_launcher_mipmap-xxhdpi", width: 144, height: 144 },
      { name: "ic_launcher_mipmap-xxxhdpi", width: 192, height: 192 },
    ];

    let completed = 0;
    icons.forEach((icon) => {
      Resizer.imageFileResizer(
        file,
        icon.width,
        icon.height,
        "PNG",
        100,
        0,
        (file) => {
          if (isTransparent) {
            setResizedTransparentIcons((prev: any) => ({
              ...prev,
              [icon.name]: file,
            }));
          } else {
            setResizedImages((prev: any) => ({
              ...prev,
              [icon.name]: file,
            }));
          }
          completed++;
          if (completed === icons.length) {
            if (isTransparent) {
              setTransparentIconProcessing(false);
            } else {
              setLogoProcessing(false);
            }
          }
        },
        "file",
        icon.width,
        icon.height,
      );
    });
  };

  const handleDelete = () => {
    deleteAppAsync({
      clientID: user.clientID,
      appID: Number(appID),
    })
      .then(() => {
        navigate("/");
        toast.success("Application Deleted");
      })
      .catch((err) => {
        console.log(err);
        toast.error("couldn't delete application");
      });
  };

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview(null);
    setResizedImages({});
  };

  const handleRemoveTransparentIcon = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (transparentIconPreview) {
      URL.revokeObjectURL(transparentIconPreview);
    }
    setTransparentIconFile(null);
    setTransparentIconPreview(null);
    setResizedTransparentIcons({});
  };

  const handleRemoveSplash = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (splashPreview) {
      URL.revokeObjectURL(splashPreview);
    }
    setSplashFile(null);
    setSplashPreview(null);
  };

  const handleImageClick = (imageUrl: string, title: string) => {
    setImagePreviewModal({ open: true, imageUrl, title });
  };

  const handleCloseImagePreview = () => {
    setImagePreviewModal({ open: false, imageUrl: null, title: "" });
  };

  // Android TV Banner (320x180) handler
  const handleAndroidTVBannerDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setAndroidTVBannerProcessing(true);
      const img = new Image();
      const previewUrl = URL.createObjectURL(file);
      img.src = previewUrl;
      img.onload = () => {
        if (img.width === 320 && img.height === 180) {
          setAndroidTVBannerFile(new File([file], "androidtv_banner.png", { type: "image/png" }));
          setAndroidTVBannerPreview(previewUrl);
          setAndroidTVBannerProcessing(false);
        } else {
          toast.error("Banner image must be exactly 320x180 pixels.");
          setAndroidTVBannerProcessing(false);
          URL.revokeObjectURL(previewUrl);
        }
      };
    }
  };

  // Android TV TV Banner (1280x720) handler
  const handleAndroidTVTVBannerDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setAndroidTVTVBannerProcessing(true);
      const img = new Image();
      const previewUrl = URL.createObjectURL(file);
      img.src = previewUrl;
      img.onload = () => {
        if (img.width === 1280 && img.height === 720) {
          setAndroidTVTVBannerFile(new File([file], "androidtv_tv_banner.png", { type: "image/png" }));
          setAndroidTVTVBannerPreview(previewUrl);
          setAndroidTVTVBannerProcessing(false);
        } else {
          toast.error("TV Banner image must be exactly 1280x720 pixels.");
          setAndroidTVTVBannerProcessing(false);
          URL.revokeObjectURL(previewUrl);
        }
      };
    }
  };

  // Android TV Store Listing Featured Graphic (1024x500) handler
  const handleAndroidTVStoreListingDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setAndroidTVStoreListingProcessing(true);
      const img = new Image();
      const previewUrl = URL.createObjectURL(file);
      img.src = previewUrl;
      img.onload = () => {
        if (img.width === 1024 && img.height === 500) {
          setAndroidTVStoreListingFile(new File([file], "androidtv_store_listing.png", { type: "image/png" }));
          setAndroidTVStoreListingPreview(previewUrl);
          setAndroidTVStoreListingProcessing(false);
        } else {
          toast.error("Store Listing Featured Graphic must be exactly 1024x500 pixels.");
          setAndroidTVStoreListingProcessing(false);
          URL.revokeObjectURL(previewUrl);
        }
      };
    }
  };

  const handleRemoveAndroidTVBanner = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (androidTVBannerPreview) {
      URL.revokeObjectURL(androidTVBannerPreview);
    }
    setAndroidTVBannerFile(null);
    setAndroidTVBannerPreview(null);
  };

  const handleRemoveAndroidTVTVBanner = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (androidTVTVBannerPreview) {
      URL.revokeObjectURL(androidTVTVBannerPreview);
    }
    setAndroidTVTVBannerFile(null);
    setAndroidTVTVBannerPreview(null);
  };

  const handleRemoveAndroidTVStoreListing = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (androidTVStoreListingPreview) {
      URL.revokeObjectURL(androidTVStoreListingPreview);
    }
    setAndroidTVStoreListingFile(null);
    setAndroidTVStoreListingPreview(null);
  };

  return (
    <main className="flex flex-col container mx-auto p-5">
      <NavBar
        goBack
        showUser={false}
      />

      <div className="w-full pl-7 mt-8 flex flex-col space-y-4">
        <header>
          <h1 className="text-xl text-black">Application Settings</h1>
          <h2 className="text-sm text-gray-500">Configure the settings for this application</h2>
        </header>

        {!isLoading ? (
          <div className="space-y-6 rounded-lg border-2 flex flex-col p-4 sm:p-6 ">
            <header>
              <h1 className="text-lg text-black">App Store</h1>
              <h2 className="text-sm text-gray-500">Add your application to the app stores</h2>
            </header>

            {(appStatus === "draft" || appStatus === "pending" || appStatus === "published") && (
              <Alert
                severity={appStatus === "draft" ? "info" : appStatus === "published" ? "success" : "warning"}
                sx={{
                  backgroundColor: appStatus === "draft" ? "#f0f4ff" : appStatus === "published" ? "#f0fdf4" : "#fef5e7",
                  color: appStatus === "draft" ? "#3b82f6" : appStatus === "published" ? "#16a34a" : "#f59e0b",
                }}
              >
                {appStatus === "draft" && <>Your application is in draft mode. It will not be visible on the app stores until it has been reviewed. Click publish to begin the review process. </>}
                {appStatus === "pending" && <>Your application is pending review. Any changes saved here will trigger another review process and a republish of your application to the app stores. </>}
                {appStatus === "published" && <>Your application is published and live on the app stores. Any changes saved here will trigger a review process before being published. </>}
              </Alert>
            )}

            <TextField 
              id="app-name"
              required
              label="Application Name"
              variant="outlined"
              placeholder="Name of the Application (Displayed on app stores)"
              value={appName}
              onChange={handleAppNameChange}
              error={!!appNameError}
              helperText={appNameError}
            />

            <Autocomplete
              multiple
              id="app-type"
              size="small"
              options={[
                // { title: "Samsung TV", value: appTypes.samsungTv },
                // { title: "Apple TV", value: appTypes.appleTv },
                { title: "Android TV", value: appTypes.androidTv},
                { title: "Fire TV", value: appTypes.fireTv },
              ]}
              value={appTargets}
              onChange={handleAppTargetsChange}
              getOptionLabel={(option) => option.title}
              filterSelectedOptions
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Target Platforms"
                  error={!!appTargetsError}
                  helperText={appTargetsError}
                />
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-gray-600 pb-1">Primary Icon</div>
                <Dropzone
                  onDrop={handleLogoDrop}
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpeg", ".jpg"],
                  }}
                  multiple={false}
                >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                  >
                    <input {...getInputProps()} />
                    {logoProcessing ? (
                      <div className="flex flex-col items-center gap-2">
                        <CircularProgress size={24} />
                        <div className="text-gray-600 text-sm">Processing image...</div>
                      </div>
                    ) : logoPreview ? (
                      <div className="relative flex flex-col items-center gap-2">
                        <IconButton
                          size="small"
                          onClick={handleRemoveLogo}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="max-h-32 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(logoPreview, "Logo Preview");
                          }}
                        />
                        <div className="text-gray-600 text-sm">Selected File: {logoFile?.name}</div>
                        <div className="text-gray-500 text-xs">Click image to enlarge • Click or drag to replace</div>
                      </div>
                    ) : (
                      <div className="text-gray-600 text-sm">Drag and drop an image here, or click to select one</div>
                    )}
                  </div>
                )}
              </Dropzone>
              </div>

              <div>
                <div className="text-gray-600 pb-1">Transparent Icon</div>
                <Dropzone
                  onDrop={handleTransparentIconDrop}
                  accept={{
                    "image/png": [".png"],
                  }}
                  multiple={false}
                >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                  >
                    <input {...getInputProps()} />
                    {transparentIconProcessing ? (
                      <div className="flex flex-col items-center gap-2">
                        <CircularProgress size={24} />
                        <div className="text-gray-600 text-sm">Processing image...</div>
                      </div>
                    ) : transparentIconPreview ? (
                      <div className="relative flex flex-col items-center gap-2">
                        <IconButton
                          size="small"
                          onClick={handleRemoveTransparentIcon}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <img
                          src={transparentIconPreview}
                          alt="Transparent icon preview"
                          className="max-h-32 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(transparentIconPreview, "Transparent Icon Preview");
                          }}
                        />
                        <div className="text-gray-600 text-sm">Selected File: {transparentIconFile?.name}</div>
                        <div className="text-gray-500 text-xs">Click image to enlarge • Click or drag to replace</div>
                      </div>
                    ) : (
                      <div className="text-gray-600 text-sm">Drag and drop a transparent PNG here, or click to select one</div>
                    )}
                  </div>
                )}
              </Dropzone>
              </div>
            </div>

            <div>
              <div className="text-gray-600 pb-1">Splash Screen (16:9)</div>
              <Dropzone
                onDrop={handleSplashDrop}
                accept={{
                  "image/png": [".png"],
                  "image/jpeg": [".jpeg", ".jpg"],
                }}
                multiple={false}
              >
                {({ getRootProps, getInputProps }) => (
                  <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                  >
                    <input {...getInputProps()} />
                    {splashProcessing ? (
                      <div className="flex flex-col items-center gap-2">
                        <CircularProgress size={24} />
                        <div className="text-gray-600 text-sm">Processing image...</div>
                      </div>
                    ) : splashPreview ? (
                      <div className="relative flex flex-col items-center gap-2">
                        <IconButton
                          size="small"
                          onClick={handleRemoveSplash}
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "white",
                            border: "1px solid #ddd",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <img
                          src={splashPreview}
                          alt="Splash screen preview"
                          className="max-h-48 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageClick(splashPreview, "Splash Screen Preview");
                          }}
                        />
                        <div className="text-gray-600 text-sm">Selected File: {splashFile?.name}</div>
                        <div className="text-gray-500 text-xs">Click image to enlarge • Click or drag to replace</div>
                      </div>
                    ) : (
                      <div className="text-gray-600 text-sm">Drag and drop splash screen image here, or click to select</div>
                    )}
                  </div>
                )}
              </Dropzone>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 p-4 sm:p-6 flex items-center justify-center h-96 animate-pulse">
            <LoaderIcon style={{ height: 40, width: 40 }} />
          </div>
        )}

        {!isLoading && isFireTVSelected() && (
          <div className="space-y-6 rounded-lg border-2 flex flex-col p-4 sm:p-6">
            <header>
              <h1 className="text-lg text-black">FireTV Settings</h1>
              <h2 className="text-sm text-gray-500">Configure FireTV-specific settings</h2>
            </header>

            <TextField
              id="firetv-icon-bg-color"
              required
              label="Icon Background Color"
              type="color"
              variant="outlined"
              value={fireTVIconBgColor}
              onChange={handleFireTVIconBgColorChange}
              error={!!fireTVIconBgColorError}
              helperText={fireTVIconBgColorError || "Select a background color for the FireTV icon"}
            />
          </div>
        )}

        {!isLoading && isAndroidTVSelected() && (
          <div className="space-y-6 rounded-lg border-2 flex flex-col p-4 sm:p-6">
            <header>
              <h1 className="text-lg text-black">Android TV Settings</h1>
              <h2 className="text-sm text-gray-500">Configure Android TV-specific settings</h2>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Banner (320x180) */}
              <div>
                <div className="text-gray-600 pb-1">Banner (320x180)</div>
                <Dropzone
                  onDrop={handleAndroidTVBannerDrop}
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpeg", ".jpg"],
                  }}
                  multiple={false}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                    >
                      <input {...getInputProps()} />
                      {androidTVBannerProcessing ? (
                        <div className="flex flex-col items-center gap-2">
                          <CircularProgress size={24} />
                          <div className="text-gray-600 text-sm">Processing...</div>
                        </div>
                      ) : androidTVBannerPreview ? (
                        <div className="relative flex flex-col items-center gap-2">
                          <IconButton
                            size="small"
                            onClick={handleRemoveAndroidTVBanner}
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <img
                            src={androidTVBannerPreview}
                            alt="Android TV Banner preview"
                            className="max-h-32 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick(androidTVBannerPreview, "Android TV Banner (320x180)");
                            }}
                          />
                          <div className="text-gray-600 text-sm">{androidTVBannerFile?.name}</div>
                          <div className="text-gray-500 text-xs">Click to enlarge • Drag to replace</div>
                        </div>
                      ) : (
                        <div className="text-gray-600 text-sm">Drag and drop a 320x180 image here</div>
                      )}
                    </div>
                  )}
                </Dropzone>
              </div>

              {/* TV Banner (1280x720) */}
              <div>
                <div className="text-gray-600 pb-1">TV Banner (1280x720)</div>
                <Dropzone
                  onDrop={handleAndroidTVTVBannerDrop}
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpeg", ".jpg"],
                  }}
                  multiple={false}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                    >
                      <input {...getInputProps()} />
                      {androidTVTVBannerProcessing ? (
                        <div className="flex flex-col items-center gap-2">
                          <CircularProgress size={24} />
                          <div className="text-gray-600 text-sm">Processing...</div>
                        </div>
                      ) : androidTVTVBannerPreview ? (
                        <div className="relative flex flex-col items-center gap-2">
                          <IconButton
                            size="small"
                            onClick={handleRemoveAndroidTVTVBanner}
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <img
                            src={androidTVTVBannerPreview}
                            alt="Android TV TV Banner preview"
                            className="max-h-32 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick(androidTVTVBannerPreview, "Android TV TV Banner (1280x720)");
                            }}
                          />
                          <div className="text-gray-600 text-sm">{androidTVTVBannerFile?.name}</div>
                          <div className="text-gray-500 text-xs">Click to enlarge • Drag to replace</div>
                        </div>
                      ) : (
                        <div className="text-gray-600 text-sm">Drag and drop a 1280x720 image here</div>
                      )}
                    </div>
                  )}
                </Dropzone>
              </div>

              {/* Store Listing Featured Graphic (1024x500) */}
              <div>
                <div className="text-gray-600 pb-1">Store Listing Featured Graphic (1024x500)</div>
                <Dropzone
                  onDrop={handleAndroidTVStoreListingDrop}
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpeg", ".jpg"],
                  }}
                  multiple={false}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div
                      {...getRootProps()}
                      className="border-2 border-dashed border-gray-300 rounded-md px-4 py-6 text-gray-500 cursor-pointer text-center"
                    >
                      <input {...getInputProps()} />
                      {androidTVStoreListingProcessing ? (
                        <div className="flex flex-col items-center gap-2">
                          <CircularProgress size={24} />
                          <div className="text-gray-600 text-sm">Processing...</div>
                        </div>
                      ) : androidTVStoreListingPreview ? (
                        <div className="relative flex flex-col items-center gap-2">
                          <IconButton
                            size="small"
                            onClick={handleRemoveAndroidTVStoreListing}
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <img
                            src={androidTVStoreListingPreview}
                            alt="Android TV Store Listing preview"
                            className="max-h-32 max-w-full object-contain rounded cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick(androidTVStoreListingPreview, "Android TV Store Listing (1024x500)");
                            }}
                          />
                          <div className="text-gray-600 text-sm">{androidTVStoreListingFile?.name}</div>
                          <div className="text-gray-500 text-xs">Click to enlarge • Drag to replace</div>
                        </div>
                      ) : (
                        <div className="text-gray-600 text-sm">Drag and drop a 1024x500 image here</div>
                      )}
                    </div>
                  )}
                </Dropzone>
              </div>
            </div>
          </div>
        )}

        {!isLoading && (
          <div className="flex flex-row-reverse">
            <Tooltip
              title={!canCreate ? "You do not have permission to publish apps" : ""}
              placement="top"
            >
              <span>
                <LoadingButton
                  variant="contained"
                  color="success"
                  disabled={!hasPendingChanges() || !isFormValid() || !canCreate}
                  onClick={handleSaveChanges}
                  loading={updateAppNameAndTargetsIsLoading || buildAppIsLoading}
                >
                  {appStatus === "draft" && "Begin Review"}
                  {appStatus === "pending" && "Save Changes"}
                </LoadingButton>
              </span>
            </Tooltip>
          </div>
        )}

        <div className="space-y-4 rounded-lg border-red-600 border-2 flex flex-col p-4 sm:p-6">
          <header>
            <h1 className="text-xl text-black">Danger Zone</h1>
            <h2 className="text-sm text-gray-500">The following actions are destructive and cannot be undone.</h2>
          </header>

          <div className="inline-block">
            <Tooltip
              title={!canDelete ? "You do not have permission to delete apps" : ""}
              placement="top"
            >
              <span>
                <button
                  className={`w-min inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-transparent border ${
                    canDelete
                      ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                      : "border-gray-400 text-gray-400 cursor-not-allowed"
                  } shadow-sm h-9 px-4 py-2`}
                  onClick={() => setShowDeleteAppModal(true)}
                  disabled={!canDelete}
                >
                  Delete
                </button>
              </span>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Delete App Confirmation Dialog */}
      <Dialog
        open={showDeleteAppModal}
        onClose={() => setShowDeleteAppModal(false)}
      >
        <DialogTitle
          sx={{
            pb: 1,
            color: "black",
          }}
        >
          Delete Application
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: "gray",
            }}
          >
            Deleting this application will remove it from all app stores and all associated data will be lost.
          </DialogContentText>
        </DialogContent>

        <DialogActions className="gap-2">
          <button
            className="w-min inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-transparent border-zinc-400 border text-zinc-400 shadow-sm hover:bg-zinc-400 hover:text-white h-9 px-4 py-2"
            onClick={() => setShowDeleteAppModal(false)}
          >
            Cancel
          </button>

          <button
            className="w-min inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-transparent border-red-600 border text-red-600 shadow-sm hover:bg-red-600 hover:text-white h-9 px-4 py-2"
            onClick={handleDelete}
          >
            Confirm
          </button>
        </DialogActions>
      </Dialog>

      {/* Save Changes Confirmation Dialog */}
      <Dialog
        open={showSaveChangesModal}
        onClose={() => setShowSaveChangesModal(false)}
      >
        <DialogTitle
          sx={{
            pb: 1,
            color: "black",
          }}
        >
          Confirm Changed Application Settings
        </DialogTitle>
        <DialogContent
          sx={{
            color: "gray",
          }}
        >
          <DialogContentText>You are about to save the following changes:</DialogContentText>
          <Box
            component="code"
            display="block"
            bgcolor="#f5f5f5"
            p={2}
            my={2}
            borderRadius={1}
          >
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              <li>Application Name: {appName}</li>
              <li>Target Platforms: {appTargets.map((target) => target.title).join(", ")}</li>
              {logoFile && <li>Primary Icon: {logoFile.name}</li>}
              {transparentIconFile && <li>Transparent Icon: {transparentIconFile.name}</li>}
              {splashFile && <li>Splash Screen: {splashFile.name}</li>}
              {isFireTVSelected() && <li>FireTV Icon Background Color: {fireTVIconBgColor}</li>}
              {androidTVBannerFile && <li>Android TV Banner: {androidTVBannerFile.name}</li>}
              {androidTVTVBannerFile && <li>Android TV TV Banner: {androidTVTVBannerFile.name}</li>}
              {androidTVStoreListingFile && <li>Android TV Store Listing Graphic: {androidTVStoreListingFile.name}</li>}
            </ul>
          </Box>
          <DialogContentText>Do you want to proceed?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowSaveChangesModal(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSaveChanges}
            color="secondary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={imagePreviewModal.open}
        onClose={handleCloseImagePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            color: "black",
          }}
        >
          {imagePreviewModal.title}
          <IconButton
            onClick={handleCloseImagePreview}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            backgroundColor: "#f5f5f5",
          }}
        >
          {imagePreviewModal.imageUrl && (
            <img
              src={imagePreviewModal.imageUrl}
              alt={imagePreviewModal.title}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </main>
  );

};

export default AppSettings;
